import React, { useState, useEffect, useRef } from 'react';
import '../App.css';
import userAvatar from '../assets/user.png';
import botAvatar from '../assets/bot.png';
import sendIcon from '../assets/send.png';
import scrollIcon from '../assets/arrow.png';
import footerImage from '../assets/footer.png';
import closeIcon from '../assets/icons.png';
import micIcon from '../assets/mic.png';
import startSound from '../assets/start.mp3';
import endSound from '../assets/end.mp3';

function ChatbotPage() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [stopTyping, setStopTyping] = useState(false);
  const [isResponseActive, setIsResponseActive] = useState(false);
  const [isIntroComplete, setIsIntroComplete] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [showEndSessionButton, setShowEndSessionButton] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);

  const messagesEndRef = useRef(null);
  const scrollRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);
  const userScrollingTimeoutRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const silenceCheckIntervalRef = useRef(null);
  const audioRef = useRef(new Audio(startSound));
  const endAudioRef = useRef(new Audio(endSound));
  const recordingStartTimeRef = useRef(null);
  const baselineRmsRef = useRef(null);

  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isAutoScrollAllowed, setIsAutoScrollAllowed] = useState(true);

  const clientId = 1;
  const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes
  const SILENCE_THRESHOLD = 0.03; // Adjusted for max RMS 0.0585
  const SILENCE_DURATION = 1500; // 1.5 seconds of silence
  const MIN_RECORDING_DURATION = 500; // 0.5 second minimum
  const CALIBRATION_DURATION = 1000; // 1 second for calibration

  // ---------------- MIC HANDLING ----------------
  const getSupportedMimeType = () => {
    if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
    if (MediaRecorder.isTypeSupported('audio/mpeg')) return 'audio/mpeg';
    return 'audio/webm';
  };

  const getAudioLevel = () => {
    if (!analyserRef.current) {
      console.log('No analyser available');
      return 0;
    }
    const dataArray = new Float32Array(analyserRef.current.fftSize);
    analyserRef.current.getFloatTimeDomainData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i] * dataArray[i];
    }
    const rms = Math.sqrt(sum / dataArray.length);
    console.log(`RMS level: ${rms.toFixed(4)}`);
    return rms;
  };

  const calibrateSilenceThreshold = async () => {
    if (!analyserRef.current) {
      console.log('No analyser for calibration');
      return SILENCE_THRESHOLD;
    }
    console.log('Calibrating silence threshold...');
    let sumRms = 0;
    let count = 0;
    const startTime = Date.now();
    
    return new Promise((resolve) => {
      const calibrationInterval = setInterval(() => {
        const rms = getAudioLevel();
        if (rms > 0) {
          sumRms += rms;
          count++;
        }
        if (Date.now() - startTime >= CALIBRATION_DURATION) {
          clearInterval(calibrationInterval);
          const avgRms = count > 0 ? sumRms / count : SILENCE_THRESHOLD;
          const calibratedThreshold = avgRms * 1.5;
          console.log(`Calibrated threshold: ${calibratedThreshold.toFixed(4)} (avg RMS: ${avgRms.toFixed(4)})`);
          resolve(Math.max(calibratedThreshold, SILENCE_THRESHOLD));
        }
      }, 50);
    });
  };

  const checkSilence = (threshold) => {
    console.log('Checking silence...');
    if (!analyserRef.current) {
      console.log('Silence check aborted: no analyser');
      clearInterval(silenceCheckIntervalRef.current);
      silenceCheckIntervalRef.current = null;
      return;
    }
    const level = getAudioLevel();
    const elapsed = Date.now() - recordingStartTimeRef.current;

    if (level < threshold && elapsed >= MIN_RECORDING_DURATION) {
      console.log(`Silence detected (RMS: ${level.toFixed(4)}, threshold: ${threshold.toFixed(4)})`);
      if (!silenceTimeoutRef.current) {
        silenceTimeoutRef.current = setTimeout(() => {
          console.log('Stopping recording due to silence');
          stopRecording();
        }, SILENCE_DURATION);
      }
    } else {
      console.log(`Sound detected (RMS: ${level.toFixed(4)}, threshold: ${threshold.toFixed(4)})`);
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
    }
  };

  const requestMicPermission = async () => {
    try {
      if (mediaStreamRef.current) {
        console.log('Reusing existing media stream');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1,
        },
      });
      mediaStreamRef.current = stream;
      const mimeType = getSupportedMimeType();
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log(`Audio chunk received: ${event.data.size} bytes`);
          audioChunksRef.current.push(event.data);
        }
      };
      mediaRecorderRef.current.onstop = handleAudioStop;

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 44100 });
      console.log('AudioContext state:', audioContextRef.current.state);
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
        console.log('AudioContext resumed');
      }
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 1024;
      analyserRef.current.smoothingTimeConstant = 0.85;
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      console.log('Microphone and audio context initialized');

      baselineRmsRef.current = await calibrateSilenceThreshold();
    } catch (error) {
      console.error('Mic access failed:', error);
      alert('Microphone access failed. Please check permissions and try again.');
    }
  };

  const startRecording = async () => {
    try {
      await requestMicPermission();
      if (!mediaRecorderRef.current || !analyserRef.current) {
        console.error('MediaRecorder or Analyser not initialized');
        alert('Failed to initialize recording. Please try again.');
        return;
      }
      audioChunksRef.current = [];
      mediaRecorderRef.current.start(50);
      setIsRecording(true);
      recordingStartTimeRef.current = Date.now();
      audioRef.current?.play().catch((err) => console.error('Start sound playback failed:', err));
      document.getElementById('mic').classList.add('recording');
      document.querySelector('.mic-shadow')?.classList.add('expand-circle');
      console.log('Recording started');

      // Clear any existing interval
      if (silenceCheckIntervalRef.current) {
        clearInterval(silenceCheckIntervalRef.current);
        silenceCheckIntervalRef.current = null;
      }

      // Start silence check interval
      silenceCheckIntervalRef.current = setInterval(() => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') {
          console.log('Silence check interval stopped: not recording');
          clearInterval(silenceCheckIntervalRef.current);
          silenceCheckIntervalRef.current = null;
          return;
        }
        checkSilence(baselineRmsRef.current || SILENCE_THRESHOLD);
      }, 200);
      console.log('Silence check interval started');
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to start recording. Please try again.');
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      console.log('Recording stopped');
    }
    setIsRecording(false);
    endAudioRef.current?.play().catch((err) => console.error('End sound playback failed:', err));
    document.getElementById('mic').classList.remove('recording');
    document.querySelector('.mic-shadow')?.classList.remove('expand-circle');
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    if (silenceCheckIntervalRef.current) {
      clearInterval(silenceCheckIntervalRef.current);
      silenceCheckIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().then(() => {
        console.log('Audio context closed');
        audioContextRef.current = null;
        analyserRef.current = null;
      }).catch((err) => console.error('Error closing audio context:', err));
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
        console.log('Media track stopped:', track.label);
      });
      mediaStreamRef.current = null;
    }
  };

  const handleAudioStop = async () => {
    if (audioChunksRef.current.length === 0) {
      console.log('No audio data recorded');
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: 'No audio recorded. Please try speaking clearly.',
          sender: 'bot',
          timestamp: getCurrentDateTime(),
        },
      ]);
      return;
    }
    const mimeType = getSupportedMimeType();
    const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
    console.log(`Audio blob created: ${audioBlob.size} bytes, type: ${mimeType}`);
    audioChunksRef.current = [];
    const reader = new FileReader();
    reader.readAsDataURL(audioBlob);
    reader.onloadend = async () => {
      const base64Audio = reader.result.split(',')[1];
      setIsLoadingResponse(true);
      try {
        const response = await fetch('/api/speech-to-text', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ audio: base64Audio, mimeType }),
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Transcription received:', data.transcript);
          if (data.transcript) {
            setInputValue(data.transcript);
            handleSubmit(data.transcript);
          } else {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                text: 'No speech detected in the audio. Please try again.',
                sender: 'bot',
                timestamp: getCurrentDateTime(),
              },
            ]);
          }
        } else {
          console.error('Speech-to-text error:', response.statusText);
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              text: 'Error transcribing audio. Please try again.',
              sender: 'bot',
              timestamp: getCurrentDateTime(),
            },
          ]);
        }
      } catch (error) {
        console.error('Transcription error:', error);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: 'Error in transcription request. Please try again.',
            sender: 'bot',
            timestamp: getCurrentDateTime(),
          },
        ]);
      } finally {
        setIsLoadingResponse(false);
      }
    };
  };

  const toggleSpeechRecognition = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const micButtonStyle = isResponseActive || isLoadingResponse ? { filter: 'grayscale(100%)' } : {};
  const sendButtonStyle = loading || isResponseActive ? { filter: 'grayscale(100%)' } : {};

  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const fetchWelcomeMessage = async () => {
    try {
      const response = await fetch('/api/get_intro?client_id=1');
      if (response.ok) {
        const data = await response.json();
        const welcomeMessage = { text: '', sender: 'bot', timestamp: getCurrentDateTime(), formVisible: false };
        setMessages([welcomeMessage]);
        typeMessage(formatResponse(data.message), () => {
          setIsTyping(false);
          setIsIntroComplete(true);
          setShowEndSessionButton(true);
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1].formVisible = true;
            return updatedMessages;
          });
        });
      } else {
        console.error('Failed to fetch welcome message:', response.statusText);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: 'Sorry, I couldn’t load the welcome message. Please try again.',
            sender: 'bot',
            timestamp: getCurrentDateTime(),
          },
        ]);
      }
    } catch (error) {
      console.error('Error fetching welcome message:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: 'Sorry, I couldn’t connect to the server. Please try again.',
          sender: 'bot',
          timestamp: getCurrentDateTime(),
        },
      ]);
    }
  };

  const endSession = () => {
    if (sessionEnded) return;
    const sessionId = sessionStorage.getItem('session_id');
    if (sessionId) {
      const url = '/api/end_session';
      const data = JSON.stringify({ client_id: clientId, session_id: sessionId });
      try {
        navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }));
        console.log('Session ended and lead updated');
      } catch (error) {
        console.error('Error sending session end beacon:', error);
      }
      sessionStorage.removeItem('session_id');
      setSessionEnded(true);
      setShowEndSessionButton(false);
      setMessages([]);
      setFormSubmitted(false);
      setIsIntroComplete(false);
      fetchWelcomeMessage();
    }
  };

  const resetInactivityTimeout = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    if (!sessionEnded) {
      inactivityTimeoutRef.current = setTimeout(() => {
        endSession();
      }, INACTIVITY_TIMEOUT);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        endSession();
        window.parent.postMessage('closeChatbot', '*');
      } catch (error) {
        console.error('Error in beforeunload:', error);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    resetInactivityTimeout();
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchWelcomeMessage();
  }, []);

  const closeChat = () => {
    try {
      endSession();
      window.parent.postMessage('closeChatbot', '*');
    } catch (error) {
      console.error('Error closing chat:', error);
    }
  };

  const handleSubmit = async (userMessageText) => {
    if (loading || isResponseActive) return;
    if (sessionEnded) {
      setSessionEnded(false);
      setMessages([]);
      setFormSubmitted(false);
      setIsIntroComplete(false);
      setShowEndSessionButton(false);
    }
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2);
      sessionStorage.setItem('session_id', sessionId);
    }
    if (!isIntroComplete) {
      clearInterval(typingIntervalRef.current);
      setStopTyping(true);
    }
    const userMessage = {
      text: userMessageText || inputValue,
      sender: 'user',
      timestamp: getCurrentDateTime(),
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInputValue('');
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    setLoading(true);
    setIsResponseActive(true);
    try {
      const response = await fetch('/api/get', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache,no-store,must-revalidate' },
        body: JSON.stringify({
          message: userMessage.text,
          client_id: clientId,
          session_id: sessionId,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const botMessage = {
          text: '',
          sender: 'bot',
          suggestions: (data.suggestions || []).slice(0, 2).map((suggestion) =>
            suggestion.replace(/^\d+[\).\s]/, '')
          ),
          timestamp: getCurrentDateTime(),
          formVisible: false,
        };
        setMessages((prevMessages) => [...prevMessages, botMessage]);
        typeMessage(formatResponse(data.response), () => {
          setLoading(false);
          setIsResponseActive(false);
          setShowEndSessionButton(true);
        });
        resetInactivityTimeout();
      } else {
        console.error('Server error:', response.statusText);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: 'Sorry, I couldn’t get a response from the server. Please try again.',
            sender: 'bot',
            timestamp: getCurrentDateTime(),
          },
        ]);
        setLoading(false);
        setIsResponseActive(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: 'Sorry, I couldn’t connect to the server. Please try again.',
          sender: 'bot',
          timestamp: getCurrentDateTime(),
        },
      ]);
      setLoading(false);
      setIsResponseActive(false);
    }
  };

  const formatResponse = (response) => {
    const isValidUrl = (url) => {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    };
    let formattedResponse = response.replace(
      /\[(.*?)\]\((https?:\/\/[^\s<>()]+)\)|(?:\((?:<|<|<)\s*Visit\s*[Tt]his\s*[Ll]ink\s*\))|(?:<(?:<|<|<)\s*Visit\s*[Tt]his\s*[Ll]ink\b\.?)/gi,
      (match, markdownText, url) => {
        const linkUrl = url || 'https://luckytech.in';
        return `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">Visit here</a>`;
      }
    );
    formattedResponse = formattedResponse.replace(/\bhhttps:\/\//g, 'https://');
    const urlPattern = /\b(https?|ftp|file):\/\/[^\s<>()]+(?![^<]*<\/a>)/gi;
    formattedResponse = formattedResponse.replace(urlPattern, (match) => {
      const cleanUrl = match.replace(/[<>()]/g, '');
      if (isValidUrl(cleanUrl)) {
        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer">Visit here</a>`;
      }
      return match;
    });
    const phonePattern = /(?:Call us:?|Phone:?|Tel:?)?\s*(\+?\d{1,3}[-.\s]?\d{3}[-.\s]?\d{3}[-.\s]?\d{4})/gi;
    formattedResponse = formattedResponse.replace(phonePattern, (match, phoneNumber) => {
      const cleanPhone = phoneNumber.replace(/[-.\s]/g, '');
      return `<a href="tel:${cleanPhone}">${match}</a>`;
    });
    const emailPattern = /(?:Email us:?)?\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;
    formattedResponse = formattedResponse.replace(emailPattern, (match, email) => {
      return `<a href="mailto:${email}">${match}</a>`;
    });
    formattedResponse = formattedResponse
      .replace(/\n\*/g, '<br>• ')
      .replace(/\n\-/g, '<br>• ')
      .replace(/\*\*/g, '')
      .replace(/\n\d+\./g, (match) => '<br>' + match.trim());
    return formattedResponse;
  };

  const typeMessage = (message, callback) => {
    let index = 0;
    setIsTyping(true);
    setStopTyping(false);
    const parser = new DOMParser();
    const doc = parser.parseFromString(message, 'text/html');
    const nodes = Array.from(doc.body.childNodes);
    let currentNodeIndex = 0;
    let currentTextIndex = 0;
    let currentText = '';
    const typeNext = () => {
      if (stopTyping) {
        clearInterval(typingIntervalRef.current);
        setIsTyping(false);
        setStopTyping(false);
        if (callback) callback();
        return;
      }
      if (currentNodeIndex >= nodes.length) {
        clearInterval(typingIntervalRef.current);
        setIsTyping(false);
        if (!isIntroComplete && !formSubmitted) {
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1].formVisible = true;
            return updatedMessages;
          });
        }
        if (callback) callback();
        return;
      }
      const currentNode = nodes[currentNodeIndex];
      if (currentNode.nodeType === Node.ELEMENT_NODE) {
        setMessages((prevMessages) => {
          const newMessage = {
            ...prevMessages[prevMessages.length - 1],
            text: prevMessages[prevMessages.length - 1].text + currentNode.outerHTML,
          };
          return [...prevMessages.slice(0, -1), newMessage];
        });
        currentNodeIndex++;
      } else if (currentNode.nodeType === Node.TEXT_NODE) {
        if (currentTextIndex === 0) {
          currentText = currentNode.textContent;
        }
        if (currentTextIndex < currentText.length) {
          setMessages((prevMessages) => {
            const newMessage = {
              ...prevMessages[prevMessages.length - 1],
              text: prevMessages[prevMessages.length - 1].text + currentText[currentTextIndex],
            };
            return [...prevMessages.slice(0, -1), newMessage];
          });
          currentTextIndex++;
        } else {
          currentNodeIndex++;
          currentTextIndex = 0;
          currentText = '';
        }
      }
      if (isAtBottom(scrollRef) && isAutoScrollAllowed) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    };
    typingIntervalRef.current = setInterval(typeNext, 40);
  };

  useEffect(() => {
    if (isIntroComplete) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isUserAtBottom = scrollHeight - scrollTop === clientHeight;
    if (isUserAtBottom || isTyping) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isTyping, isIntroComplete]);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
      if (!isNearBottom) {
        setIsUserScrolling(true);
        setIsAutoScrollAllowed(false);
        clearTimeout(userScrollingTimeoutRef.current);
        userScrollingTimeoutRef.current = setTimeout(() => {
          setIsUserScrolling(false);
        }, 2000);
      } else {
        setIsUserScrolling(false);
        setIsAutoScrollAllowed(true);
      }
      setShowScrollButton(!isNearBottom);
    };
    const ref = scrollRef.current;
    ref.addEventListener('scroll', handleScroll);
    return () => {
      ref.removeEventListener('scroll', handleScroll);
      clearTimeout(userScrollingTimeoutRef.current);
    };
  }, []);

  const isAtBottom = (ref) => {
    if (!ref.current) return false;
    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    return scrollHeight - scrollTop - clientHeight < 50;
  };

  const handleSuggestionClick = (suggestion) => {
    handleSubmit(suggestion);
  };

  return (
    <div className="chatcontainer">
      <div className="card">
        <div className="card-header msg_head">
          <div className="d-flex bd-highlight">
            <div className="img_cont"></div>
            <div className="ml-auto">
              <button type="button" className="close" aria-label="Close" id="closeChat" onClick={closeChat}>
                <img src={closeIcon} alt="Close" loading="lazy" />
              </button>
            </div>
          </div>
        </div>
        <div id="messageFormeight" className="card-body msg_card_body" ref={scrollRef}>
          {messages.map((msg, index) => (
            <div key={index} className="message-wrapper">
              <div className={`d-flex justify-content-${msg.sender === 'user' ? 'end' : 'start'} mb-2`}>
                {msg.sender === 'bot' && (
                  <div className="img_cont_msg">
                    <img src={botAvatar} className="rounded-circle user_img_msg" alt="Bot" loading="lazy" />
                  </div>
                )}
                <div className={`msg_cotainer${msg.sender === 'user' ? '_send' : ''}`}>
                  <div style={{ whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: msg.text }} />
                </div>
                {msg.sender === 'user' && (
                  <div className="img_cont_msg">
                    <img src={userAvatar} className="rounded-circle user_img_msg" alt="User" loading="lazy" />
                  </div>
                )}
              </div>
              {msg.timestamp && (
                <div className={`d-flex justify-content-${msg.sender === 'user' ? 'end' : 'start'} mt-1`}>
                  <span className={`msg_time${msg.sender === 'user' ? '_send' : ''}`}>
                    {msg.timestamp}
                  </span>
                </div>
              )}
              {msg.suggestions?.length > 0 && !isTyping && (
                <div className="suggestion-container">
                  {msg.suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      className="suggestion-button"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {loading && !isTyping && (
            <div className="d-flex justify-content-start mb-2">
              <div className="img_cont_msg">
                <img src={botAvatar} className="rounded-circle user_img_msg" alt="Bot" loading="lazy" />
              </div>
              <div className="msg_cotainer">
                <div className="loader"></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        {showScrollButton && (
          <button
            onClick={() => {
              scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
              setIsAutoScrollAllowed(true);
            }}
            className="scroll-to-bottom"
          >
            <img src={scrollIcon} alt="Scroll Down" loading="lazy" />
          </button>
        )}
        <div className="card-footer">
          <form id="messageArea" className="input-group" onSubmit={(e) => { e.preventDefault(); handleSubmit(inputValue); }}>
            <button
              type="button"
              id="mic"
              className={`mic-button ${isRecording ? 'recording' : ''} ${isLoadingResponse ? 'loading' : ''}`}
              onClick={toggleSpeechRecognition}
              style={micButtonStyle}
              disabled={isLoadingResponse}
            >
              <div className="mic-shadow"></div>
              <img src={micIcon} alt="Microphone" style={{ width: '30px', height: '30px' }} />
            </button>
            <input
              type="text"
              id="text"
              name="msg"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="form-control type_msg"
              placeholder="Ask me anything..."
              disabled={loading || isResponseActive}
              aria-label="Type your message"
            />
            <button
              type="submit"
              className="input-group-text send_btn"
              disabled={loading || isResponseActive}
              aria-label="Send message"
            >
              <img src={sendIcon} alt="Send" loading="lazy" style={sendButtonStyle} />
            </button>
          </form>
          <div className="text-center footer">
            <a href="https://www.papermoon.in" target="_blank" rel="noopener noreferrer">
              <img src={footerImage} alt="Footer" className="footer-img" loading="lazy" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatbotPage;